import { useMemo } from "react";
import axios from "axios";
import { useApiUrl } from "@refinedev/core";
import { getUserInfoFromCache } from "utility/helper";
import {
  WebhookDeliveryDetail,
  WebhookDeliveryPage,
  WebhookDeliveryQuery,
  WebhookSubscription,
  WebhookSubscriptionRequest,
  WebhookTestResult,
} from "./types";

type Method = "GET" | "POST" | "PUT" | "DELETE";

export const webhookKeys = {
  list: ["webhooks", "subscriptions"] as const,
  eventTypes: ["webhooks", "event-types"] as const,
  subscription: (id: number) => ["webhooks", "subscription", id] as const,
  deliveries: (id: number) => ["webhooks", "deliveries", id] as const,
  delivery: (id: number) => ["webhooks", "delivery", id] as const,
};

export function useWebhookApi() {
  const baseUrl = useApiUrl();

  return useMemo(() => {
    const request = async <T>(method: Method, path: string, body?: unknown): Promise<T> => {
      try {
        const response = await axios.request<T>({
          baseURL: baseUrl,
          method,
          url: path,
          data: body,
          headers: {
            Authorization: `Bearer ${getUserInfoFromCache()?.token ?? ""}`,
          },
        });
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          throw error.response?.data ?? error;
        }
        throw error;
      }
    };

    return {
      listEventTypes: () => request<string[]>("GET", "webhooks/event-types"),
      listSubscriptions: () => request<WebhookSubscription[]>("GET", "webhooks/subscriptions"),
      getSubscription: (id: number) => request<WebhookSubscription>("GET", `webhooks/subscriptions/${id}`),
      createSubscription: (body: WebhookSubscriptionRequest) => request<WebhookSubscription>("POST", "webhooks/subscriptions", body),
      updateSubscription: (id: number, body: WebhookSubscriptionRequest) =>
        request<WebhookSubscription>("PUT", `webhooks/subscriptions/${id}`, body),
      deleteSubscription: (id: number) => request<void>("DELETE", `webhooks/subscriptions/${id}`),
      regenerateKey: (id: number) => request<WebhookSubscription>("POST", `webhooks/subscriptions/${id}/regenerate-key`),
      enableSubscription: (id: number) => request<WebhookSubscription>("POST", `webhooks/subscriptions/${id}/enable`),
      disableSubscription: (id: number) => request<WebhookSubscription>("POST", `webhooks/subscriptions/${id}/disable`),
      sendTest: (id: number) => request<WebhookTestResult>("POST", `webhooks/subscriptions/${id}/test`),
      listDeliveries: (id: number, query: WebhookDeliveryQuery) => {
        const params = new URLSearchParams();
        params.set("page", String(query.page));
        params.set("pageSize", String(query.pageSize));
        if (query.status) {
          params.set("status", query.status);
        }
        if (query.eventType) {
          params.set("eventType", query.eventType);
        }
        return request<WebhookDeliveryPage>("GET", `webhooks/subscriptions/${id}/deliveries?${params.toString()}`);
      },
      getDelivery: (id: number) => request<WebhookDeliveryDetail>("GET", `webhooks/deliveries/${id}`),
      redeliver: (id: number) => request<WebhookDeliveryDetail>("POST", `webhooks/deliveries/${id}/redeliver`),
    };
  }, [baseUrl]);
}
